/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import Head from 'next/head';
import {YES_NO} from '@ion/core/src/types/general';

/**
 * Defines the {@link PageViewportMeta} props and their respective types. Note,
 * these props generally do not need to be overridden.
 *
 * @param width Controls the size of the viewport. It can be set to a specific
 *        number of pixels like width=600 or to the special value device-width
 * @param initialScale Controls the zoom level when the page is first loaded.
 * @param minimumScale Controls the extent to which users can zoom the page out.
 * @param maximumScale Controls the extent to which users can zoom the page in.
 * @param userScalable Determines if the user is allowed to scale the page.
 * @param shrinkToFit When using "width=device-width" the page will scale down
 *        to fit content that overflows the viewport bounds. You can override
 *        this behavior by setting `shrinkToFit` to `no` (default). This added
 *        value will prevent the page from scaling to fit the viewport.
 */
export type PageViewportMetaProps = {
  width?: string | number,
  initialScale?: number,
  minimumScale?: number,
  maximumScale?: number,
  userScalable?: YES_NO,
  shrinkToFit?: YES_NO
};

/**
 * The `PageViewportMeta` component provides a standardized Viewport Meta Element which
 * is to be used by all iOn Class Applications. Generally, applications should not need
 * to override any of the accepted prop values, though all supported values are fully
 * customizable if required.
 *
 * ```typescript
 *   function App({Component, pageProps}: ApplicationPage) {
 *     ...
 *     return (
 *       <>
 *         <PageViewportMeta />
 *         ...
 *       </>
 *     }
 * ```
 *
 * {@link PageViewportMetaProps}
 * @see https://developer.mozilla.org/en-US/docs/Glossary/Viewport
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Viewport_meta_tag
 */
const PageViewportMeta: React.FC<PageViewportMetaProps> = ({
  width = 'device-width',
  initialScale = 1.0,
  minimumScale = 1.0,
  maximumScale = 1.0,
  userScalable = 'no',
  shrinkToFit = 'no'
}) => {
  const attributes = `
    width=${width},
    initial-scale=${initialScale},
    minimum-scale=${minimumScale},
    maximum-scale=${maximumScale},
    user-scalable=${userScalable},
    shrink-to-fit=${shrinkToFit}
  `.replace(/\s\s+/g, ' ').trim();
  return (
    <Head>
      <meta name='viewport' content={attributes} />
    </Head>
  );
};
export default React.memo(PageViewportMeta);
